import { faCircleInfo } from '@fortawesome/pro-duotone-svg-icons';
import { faChevronDown, faChevronLeft, IconDefinition } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Autocomplete, Box, InputAdornment, MenuItem, OutlinedInput, Select, SelectChangeEvent, TextField, Typography, useMediaQuery } from '@mui/material';
import dayjs, { Dayjs } from 'dayjs';
import { useFormik } from 'formik';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { sxFormError, sxLabel, sxMenuItem, sxSubTitle } from '../../../../../assets/styles/CommonSxProps';
import DefaultButton from '../../../../../components/Accessibility/DefaultButton';
import LightTooltip from '../../../../../components/LightTooltip';
import ModalAction from '../../../../../components/ModalAction';
import ModalFeedBack from '../../../../../components/ModalFeedBack';
import { ProcedimentoRealizado } from '../../../../../interfaces/entities/Reembolso/ProcedimentoRealizado';
import { ProcedimentoReembolso } from '../../../../../interfaces/entities/Reembolso/ProcedimentoReembolso';
import { StepDadosProcedimentoGenericoProps } from '../../../../../interfaces/props/Financeiro/Reembolso/StepDadosProcedimentosProps';
import { DadosProcedimentoValidation } from '../../../../../interfaces/validation/Reembolso/DadosProcedimentoValidation';
import { getListaProcedReembolso } from '../../../../../services/Financeiro/ReembolsoService';
import { formatStringToReaisNumberFormat } from '../../../../../utils/MoneyFormat';
import { sxButton, sxStartAdornment } from '../sxProps';
import ProcedimentoReembolsoCard from './ProcedimentoReembolsoCard';
import './styles.css';
import { sxAutocomplete, sxInputSmall } from './sxProps';
import ValorTotalReembolso from './ValorTotalReembolso';
import DateInput from '../../../../../components/DateInput';
import { debounce } from '../../../../../utils/Debounce';
import moment from 'moment';

const RenderDateFields: React.FC<{quantidade: number, onChange: (value: Dayjs[]) => void}> = React.memo(({ quantidade, onChange }) => {

  const [datasProcedimentos, setDatasProcedimentos] = useState<Dayjs[]>(new Array(quantidade).fill(null));

  useEffect(() => {
    setDatasProcedimentos(new Array(quantidade).fill(null))
  }, [quantidade]);

  const handleDateChange = (index: number, date: Dayjs) => {
    if (date && date.isValid()) {
      const newDates = datasProcedimentos.map((s, i) => {
        if (i === index) return date;
        else return s;
      });
      setDatasProcedimentos(newDates);
      onChange(newDates);
    }
  };

  return Array.from({ length: quantidade }, (_, i) => (
    <Box key={`procedimento-box-${i}`}>
      <DateInput
        label={`Data do procedimento ${i+1}`}
        value={datasProcedimentos[i] || null}
        onChange={(date) => handleDateChange(i, date || dayjs())}
        maxDate={dayjs(moment().format('YYYY-MM-DD'))}
      />
    </Box>
  ));
}, (prevProps, nextProps) => prevProps.quantidade === nextProps.quantidade);

const StepDadosProcedimento: React.FC<StepDadosProcedimentoGenericoProps> = ({
  handleBack, handleProceed, idPedidoReembolso, especialidades, userRequestedToEditInfos, procedimentosRealizados, valorTotalProp
}) => {

  const tooltipProcedimento: string = "Escreva a palavra que deseja buscar e selecione entre as opções exibidas o item realizado. Você pode buscar pelo nome ou pelo código TUSS.";
  const tooltipValor: string = "Coloque o valor total que você pagou referente a este item, conforme descrito no recibo/nota fiscal. Caso tenha informado mais de uma data, o valor informado deve ser a soma de todas sessões de atendimentos. O valor a ser reembolsado será calculado posteriormente, de acordo com as regras previstas no seu contrato.";
  const [addingItem, setAddingItem] = useState<boolean>(true);
  const [lstProcedimentos, setLstProcedimentos] = useState<ProcedimentoReembolso[]>([]);
  const [lstProcedimentosRealizados, setLstProcedimentosRealizados] = useState<ProcedimentoRealizado[]>([]);
  const [valorTotal, setValorTotal] = useState<string>();
  const [datasProcedimentos, setDatasProcedimentos] = useState<Dayjs[]>([]);
  const mobileScreen = useMediaQuery('(max-width: 768px)');
  const topOfPageRef = useRef<HTMLDivElement>(null);

  const [modalFeedback, setModalFeedback] = useState<null | {
    icon?: 'error' | 'success' | 'warning', title?: string, textButton?: string, subTitle?: string,
    closeAction: () => void, showButtonModal: boolean, buttonAction?: () => void
  }>(null);

  const [modalAction, setModalAction] = useState<null | {
    handleShowModal?: () => void; textOutlinedButton: string; textContainedButton: string;
    title: string; subTitle?: string; handleOutlinedButton?: () => void; handleContainedButton?: () => void; icon: "error" | "success" | "warning"
  }>(null);

  const formik = useFormik<ProcedimentoRealizado>({
    initialValues: {
      procedimento: null,
      quantidade: '',
      valorPago: '',
      especialidade: null,
      datas: []
    },
    onSubmit: (values) => {
      const datasExtraidas = datasProcedimentos.filter((data): data is NonNullable<typeof data> => data !== null);
      addProcedToRealizedList(values, datasExtraidas);
      showProcedList();
      topOfPageRef?.current?.focus();
    },
    validationSchema: DadosProcedimentoValidation,
    validateOnChange: false
  });

  const fetchListaProced = async (descrPesquisa: string) => {
    const response = await getListaProcedReembolso(descrPesquisa, idPedidoReembolso);
    if (response?.status === 200) {
      setLstProcedimentos(response.data);
    } else {
      console.error(response);
      openModalError('Erro', 'Ocorreu um erro. Tente novamente em instantes.');
    }
  }

  const fetchListaProcedDebounced = useCallback(debounce(fetchListaProced, 400), []);

  const filterProced = (event: React.SyntheticEvent, query: string) => {
    if (query.length >= 5) {
      fetchListaProcedDebounced(query);
    }
  }

  useEffect(() => {
    verifyIfIsEditing();
  }, [])

  const verifyIfIsEditing = () => {
    if (userRequestedToEditInfos && procedimentosRealizados && valorTotalProp) {
      setLstProcedimentosRealizados(procedimentosRealizados);
      setValorTotal(valorTotalProp);
      setAddingItem(false);
    }
  }

  const renderNewForm = () => {
    setAddingItem(true);
    setDatasProcedimentos([]);
    formik.resetForm();
  }

  const addProcedToRealizedList = (procedDetalhes: ProcedimentoRealizado, datasProceds: Dayjs[]) => {
    const valorPagoNormalized: number = parseFloat(procedDetalhes.valorPago.replace(/[^0-9.-]+/g, ""));
    const filledProced = {
      ...procedDetalhes,
      valorPago: valorPagoNormalized.toString(),
      datas: datasProceds
    };
    setLstProcedimentosRealizados(prevState => [...prevState, filledProced]);
    setValorTotal(prevValorTotal => {
      const prevValorTotalNormalized: number = parseFloat((prevValorTotal || '0'));

      const novoValorTotal = prevValorTotalNormalized + valorPagoNormalized;

      return novoValorTotal.toString();
    });
  }

  const removeProcedFromRealizedList = (indice: number) => {
    setValorTotal(prevValorTotal => {
      const procedimentoRemoved = lstProcedimentosRealizados[indice]; // Acessa o procedimento removido antes da atualização do estado
      const valorPagoNormalized: number = parseFloat(procedimentoRemoved.valorPago.replace(/[^0-9.-]+/g, ""));

      const prevValorTotalNormalized: number = parseFloat(
        (prevValorTotal || '0').replace(',', '.'));

      const novoValorTotal = prevValorTotalNormalized - valorPagoNormalized;

      const valorTotalNumerico = novoValorTotal.toFixed(2).replace('.', ',');

      return valorTotalNumerico;
    });

    setLstProcedimentosRealizados(prevState => {
      const updatedState = [...prevState];
      updatedState.splice(indice, 1);
      return updatedState;
    });

    handleListaProcedsReachedZero(lstProcedimentosRealizados.length - 1);
    handleCloseModalAction();
  };

  const showProcedList = () => {
    setAddingItem(false);
  }

  const selectProced = (event: React.SyntheticEvent, value: ProcedimentoReembolso | null) => {
    formik.setFieldValue('procedimento', value);
  }
  const selectEspecialidade = (event: SelectChangeEvent<string>) => {
    const idEspecialidade = event.target.value;
    const especialidadeSelecionada = especialidades.find((especialidade) => especialidade.id === idEspecialidade);
    formik.setFieldValue('especialidade', especialidadeSelecionada)
  }

  const handleListaProcedsReachedZero = (lstLength: number) => {
    if (lstLength === 0) {
      formik.resetForm();
      setAddingItem(true);
    }
  }

  const openModalError = (title: string, subtitle: string) => {
    setModalFeedback({
      icon: 'error',
      title: title,
      subTitle: subtitle,
      textButton: 'Fechar',
      showButtonModal: true,
      closeAction: () => setModalFeedback(null),
      buttonAction: () => setModalFeedback(null)
    });
  }

  const handleDeleteProced = (indice: number) => {
    openModalDeleteProced('Excluir procedimento', 'Tem certeza que deseja excluir o procedimento?', indice);
  }

  const openModalDeleteProced = (title: string, subtitle: string, indiceProced: number) => {
    setModalAction({
      title: title,
      subTitle: subtitle,
      textOutlinedButton: 'Cancelar',
      textContainedButton: 'Excluir procedimento',
      handleContainedButton: () => removeProcedFromRealizedList(indiceProced),
      handleOutlinedButton: handleCloseModalAction,
      handleShowModal: handleCloseModalAction,
      icon: 'warning'
    })
  }

  const handleCloseModalAction = () => {
    setModalAction(null);
  }

  const handleCostChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    const digits = value.replace(/^0+/, '');
    const noDot = digits.replace(/\./g, '');
    const onlyDigits = noDot.replace(/[^\d]/g, '');
    if (onlyDigits.length === 0 || onlyDigits === '0') {
      formik.setFieldValue('valorPago', '');
    }
    else if (onlyDigits.length === 1) {
      formik.setFieldValue('valorPago', `0.0${onlyDigits}`);
    }
    else if (onlyDigits.length === 2) {
      formik.setFieldValue('valorPago', `0.${onlyDigits}`);
    }
    else {
      let maskedValue = '';
      const len = onlyDigits.length;
      for (let i = 0; i < len; i++) {
        maskedValue += onlyDigits[i];
        if ((len - i) === 3 && i !== len - 1) {
          maskedValue += '.';
        }
      }
      formik.setFieldValue('valorPago', maskedValue);
    }
  }

  const handleDateChange = useCallback((data: Dayjs[]) => {
    setDatasProcedimentos(data);
  }, []);

  const handleChangeQuantidade = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    formik.setFieldValue('quantidade', event.target.value);
    setDatasProcedimentos([]);
  }

  const formattedCost = formatStringToReaisNumberFormat(formik.values.valorPago);

  return (
    <>
      <form className='feature-container' onSubmit={formik.handleSubmit}>
        <>
          <Typography component="h2" sx={sxSubTitle} ref={topOfPageRef}>Cadastrar procedimentos para reembolso</Typography>
          {addingItem &&
            <>
              <div className='input-holder'>
                <div className='input-label-holder'>
                <Typography sx={sxLabel} tabIndex={0}>
                  Procedimento realizado &nbsp;
                </Typography>
                </div>
                <label aria-label="procedimento realizado" htmlFor="autocomplete-procedimentos" />
                <Autocomplete
                  disablePortal
                  value={formik.values.procedimento}
                  fullWidth={mobileScreen}
                  id="autocomplete-procedimentos"
                  onChange={selectProced}
                  onInputChange={filterProced}
                  options={lstProcedimentos}
                  getOptionLabel={(option: ProcedimentoReembolso) => option.descr}
                  isOptionEqualToValue={(option, value) => option.codProced === value.codProced}
                  sx={sxAutocomplete}
                  renderInput={(params) =>
                    <TextField {...params}
                      placeholder='Exemplos: Consulta (procedimento) ou 99999999 (código TUSS)'
                      error={!!formik.errors.procedimento}
                    />}
                />
                <div className='tooltip-holder left-procedimento'>
                  <LightTooltip
                    title={tooltipProcedimento}
                  >
                    <FontAwesomeIcon icon={faCircleInfo as IconDefinition} className='fa-info-icon-18x18' />
                  </LightTooltip>
                </div>
                <Typography sx={sxFormError}>{formik.errors.procedimento}</Typography>
              </div>
              {formik.values.procedimento?.isHabilitaEspecialidade &&
                <div className='input-holder'>
                  <Typography sx={sxLabel}>
                    Especialidade
                  </Typography>
                  <Select
                    sx={sxInputSmall}
                    value={formik.values.especialidade?.id}
                    fullWidth={mobileScreen}
                    onChange={selectEspecialidade}
                    displayEmpty
                    renderValue={(selected) => {
                      if (!selected) {
                        return <span className="placeholder">Selecione a especialidade</span>;
                      }
                      const especialidadeSelecionada = especialidades.find(espec => espec.id === selected);
                      return especialidadeSelecionada?.descricao || 'Opção inválida';
                    }}
                    IconComponent={(props) => <FontAwesomeIcon icon={faChevronDown} {...props} />}
                  >
                    {!!especialidades.length &&
                      especialidades.map((especialidade, index) => (
                        <MenuItem key={index} sx={sxMenuItem} value={especialidade.id}>
                          {especialidade.descricao}
                        </MenuItem>
                      ))}
                  </Select>
                  <Typography sx={sxFormError}>{formik.errors.especialidade}</Typography>
                </div>
              }
              <div className='input-holder'>
                <Typography sx={sxLabel} tabIndex={0}>
                  Quantidade &nbsp;
                </Typography>
                <label htmlFor="quantidade-input" aria-label="quantidade" />
                <OutlinedInput
                  type={'number'}
                  id="quantidade-input"
                  value={formik.values.quantidade}
                  fullWidth={mobileScreen}
                  placeholder='99'
                  onChange={handleChangeQuantidade}
                  name='numeroDocumento'
                  error={!!formik.errors.quantidade}
                  maxRows={1}
                  inputProps={{ maxLength: 2 }}
                  sx={sxInputSmall}
                />
                <Typography sx={sxFormError}>{formik.errors.quantidade}</Typography>
              </div>
              {!!formik.values.quantidade &&
                <div>
                  <Typography sx={sxLabel}>
                    Data dos procedimentos
                  </Typography>
                  <Box className='half-divided-input' maxWidth={'600px'}>
                    {!!formik.values.quantidade &&
                    <RenderDateFields 
                      quantidade={parseInt(formik.values.quantidade)} 
                      onChange={handleDateChange} 
                    />}
                  </Box>
                </div>
              }
              <div className='input-holder'>
                <div className='input-label-holder'>
                  <Typography sx={sxLabel} tabIndex={0}>
                    Valor pago &nbsp;
                  </Typography>
                </div>
                <label htmlFor="valor-pago-input" aria-label="valor pago" />
                <OutlinedInput
                  type={'text'}
                  id="valor-pago-input"
                  value={formik.values.valorPago ? formattedCost : ''}
                  fullWidth={mobileScreen}
                  placeholder='99,99'
                  onChange={handleCostChange}
                  name='valorPago'
                  error={!!formik.errors.valorPago}
                  startAdornment={formik.values.valorPago === '' ? null : <InputAdornment sx={sxStartAdornment} position="start">R$</InputAdornment>}
                  maxRows={1}
                  inputProps={{ maxLength: 20 }}
                  sx={sxInputSmall}
                />
                <div className='tooltip-holder left-valor-pago'>
                  <LightTooltip
                    title={tooltipValor}
                  >
                    <FontAwesomeIcon icon={faCircleInfo as IconDefinition} className='fa-info-icon-18x18' />
                  </LightTooltip>
                </div>
                <Typography sx={sxFormError}>{formik.errors.valorPago}</Typography>
              </div>
              <DefaultButton
                variant="contained"
                size="large"
                type='submit'
                fullWidth={mobileScreen}
                sx={sxButton}
                disabled={!(!!formik.values.procedimento && !!formik.values.quantidade && !!formik.values.valorPago &&
                  (datasProcedimentos.length >= Number(formik.values.quantidade)) &&
                  (datasProcedimentos.slice(0, Number(formik.values.quantidade)).every(data => !!data && data.isValid()))) ||
                  (Number(formik.values.quantidade) < 1)
                }
                text='INCLUIR ITEM'
              />
            </>
          }
          {(!!lstProcedimentosRealizados.length && !!valorTotal) &&
            <div className='proceds-container'>
              {lstProcedimentosRealizados.map((procedimento, index) =>
                <ProcedimentoReembolsoCard key={index}
                  procedimentoRealizado={procedimento} even={index % 2 === 0}
                  indice={index} handleDeleteProced={handleDeleteProced}
                />
              )}
              <ValorTotalReembolso valor={valorTotal} qtdItens={lstProcedimentosRealizados.length} />
            </div>
          }
          {!addingItem && lstProcedimentosRealizados.length > 0 &&
            <DefaultButton
              variant="outlined"
              size="large"
              onClick={renderNewForm}
              fullWidth={mobileScreen}
              text='INCLUIR NOVO ITEM'
            />
          }
          <DefaultButton
            variant="contained"
            size="large"
            onClick={() => handleProceed(lstProcedimentosRealizados, valorTotal || '')}
            disabled={!lstProcedimentosRealizados.length}
            fullWidth={mobileScreen}
            text='CONTINUAR'
            sx={sxButton}
          />
          <button className="back-button" onClick={() => handleBack()} aria-label="voltar para dados do prestador">
            <FontAwesomeIcon icon={faChevronLeft} />&nbsp;&nbsp;<u>Voltar</u>
          </button>
        </>
      </form>
      <ModalFeedBack
        showModal={!!modalFeedback}
        handleShowModal={modalFeedback?.closeAction}
        title={modalFeedback?.title as string}
        subTitle={modalFeedback?.subTitle as string}
        icon={modalFeedback?.icon as 'success' | 'error' | 'warning'}
        showButtonModal={modalFeedback?.showButtonModal}
        textButton={modalFeedback?.textButton as string}
        handleButtonModal={modalFeedback?.buttonAction}
      />
      <ModalAction
        showModal={!!modalAction}
        handleShowModal={modalAction?.handleShowModal}
        icon={modalAction?.icon}
        textOutlinedButton={modalAction?.textOutlinedButton || ''}
        textContainedButton={modalAction?.textContainedButton || ''}
        title={modalAction?.title || ''}
        subTitle={modalAction?.subTitle || ''}
        handleOutlinedButton={modalAction?.handleOutlinedButton}
        handleContainedButton={modalAction?.handleContainedButton}
      />
    </>
  )
}

export default StepDadosProcedimento;